import config from "../../config";
import { getlist, post } from "../../contexts/apicontexts";
import { normalizeTicketData } from "./ticketUtils";

const DEFAULT_PAGE_SIZE = 10;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeFilterOption = (item) => ({
  id: toNumber(item?.id ?? item?.Id),
  name: String(item?.name ?? item?.Name ?? "").trim(),
  organizationId: toNumber(item?.organizationId ?? item?.OrganizationId),
});

const normalizeTicketSummary = (summary) => ({
  total: toNumber(summary?.total ?? summary?.Total),
  new: toNumber(summary?.new ?? summary?.New),
  inProgress: toNumber(summary?.inProgress ?? summary?.InProgress),
  onHold: toNumber(summary?.onHold ?? summary?.OnHold),
  resolved: toNumber(summary?.resolved ?? summary?.Resolved),
  myTicketCount: toNumber(summary?.myTicketCount ?? summary?.MyTicketCount),
  unreadTicketCount: toNumber(summary?.unreadTicketCount ?? summary?.UnreadTicketCount),
  unreadReplyCount: toNumber(summary?.unreadReplyCount ?? summary?.UnreadReplyCount),
});

const normalizePagedTicketResult = (result) => ({
  items: Array.isArray(result?.items ?? result?.Items) ? (result?.items ?? result?.Items).map(normalizeTicketData) : [],
  totalCount: toNumber(result?.totalCount ?? result?.TotalCount),
  totalPages: toNumber(result?.totalPages ?? result?.TotalPages),
  pageNumber: toNumber(result?.pageNumber ?? result?.PageNumber, 1) || 1,
  pageSize: toNumber(result?.pageSize ?? result?.PageSize, DEFAULT_PAGE_SIZE) || DEFAULT_PAGE_SIZE,
  hasNextPage: Boolean(result?.hasNextPage ?? result?.HasNextPage),
  hasPreviousPage: Boolean(result?.hasPreviousPage ?? result?.HasPreviousPage),
  summary: normalizeTicketSummary(result?.summary ?? result?.Summary ?? {}),
  organizations: Array.isArray(result?.organizations ?? result?.Organizations)
    ? (result?.organizations ?? result?.Organizations).map(normalizeFilterOption).filter((item) => item.id > 0 && item.name)
    : [],
  customers: Array.isArray(result?.customers ?? result?.Customers)
    ? (result?.customers ?? result?.Customers).map(normalizeFilterOption).filter((item) => item.id > 0 && item.name)
    : [],
});

const buildTicketPagedPayload = ({
  userId = "",
  pageNumber = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  searchText = "",
  status = "",
  organizationId = 0,
  customerId = 0,
} = {}) => ({
  userId: userId || "",
  pageNumber: Number(pageNumber || 1),
  pageSize: Number(pageSize || DEFAULT_PAGE_SIZE),
  searchText: searchText || "",
  status: status && status !== "All" ? status : "",
  organizationId: Number(organizationId || 0) || null,
  customerId: Number(customerId || 0) || null,
  sortDirection: "DESC",
});

export const fetchTicketPagedList = async (options = {}) => {
  const payload = buildTicketPagedPayload(options);
  const endpoint = options.assignedView ? "Ticket/getassignedpaged" : "Ticket/getpaged";
  const response = await post(`${config.baseurl}${endpoint}`, payload);

  if (response?.status === 200) {
    return normalizePagedTicketResult(response.data || {});
  }

  throw new Error(response?.message || "Tickets could not be loaded.");
};

export const fetchAccessibleCustomers = async (scope = "all") => {
  const endpoint = scope === "mine" ? "Customer/getmylist" : "Customer/getlist";
  const response = await getlist(`${config.baseurl}${endpoint}`);
  if (response?.status !== 200 || !Array.isArray(response.data)) return [];

  return response.data
    .map((item) => ({
      id: toNumber(item?.id ?? item?.Id),
      name: String(item?.name ?? item?.Name ?? "").trim(),
      organizationId: toNumber(item?.organizationId ?? item?.OrganizationId),
    }))
    .filter((item) => item.id > 0 && item.name);
};

export const fetchOrganizations = async () => {
  const response = await getlist(`${config.baseurl}Organizations/getlist`);
  if (response?.status !== 200 || !Array.isArray(response.data)) return [];

  return response.data
    .map((item) => ({
      id: toNumber(item?.id ?? item?.Id),
      name: String(item?.name ?? item?.Name ?? "").trim(),
    }))
    .filter((item) => item.id > 0 && item.name);
};

export const DEFAULT_TICKET_PAGE_SIZE = DEFAULT_PAGE_SIZE;
export const TICKET_PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
